/**
 * 图片压缩工具
 * 基于 Canvas 对图片进行等比缩放 + JPEG 质量压缩
 */

/** 默认最大边长（px），超过此值的图片会等比缩放 */
const DEFAULT_MAX_EDGE = 800;
/** 默认 JPEG 压缩质量（0~1） */
const DEFAULT_QUALITY = 0.7;
/** 小于此大小（字节）的图片直接跳过压缩 */
const SKIP_COMPRESS_THRESHOLD = 50 * 1024; // 50KB

/**
 * 压缩图片文件
 *
 * @param file      原始图片 File 对象
 * @param maxEdge   最大边长（px），默认 1280
 * @param quality   JPEG 压缩质量，默认 0.8
 * @returns 压缩后的 File 对象；若无需压缩则返回原文件
 */
export async function compressImage(
  file: File,
  maxEdge: number = DEFAULT_MAX_EDGE,
  quality: number = DEFAULT_QUALITY,
): Promise<File> {
  // 非图片类型直接返回
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // 小文件跳过压缩，避免无谓开销
  if (file.size <= SKIP_COMPRESS_THRESHOLD) {
    console.log(`[imageCompress] 文件 ${file.name} 大小 ${(file.size / 1024).toFixed(1)}KB，低于阈值跳过压缩`);
    return file;
  }

  // PNG 透明通道图：仅缩放，不转 JPEG（保留 PNG 格式避免黑底）
  const isPng = file.type === 'image/png';

  try {
    const bitmap = await loadImage(file);
    const { width: srcW, height: srcH } = bitmap;

    // 计算缩放后的尺寸
    const { width, height } = calcScaledSize(srcW, srcH, maxEdge);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Canvas 不支持，降级返回原文件
      return file;
    }

    // 白底填充（JPEG 不支持透明）
    if (!isPng) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
    }

    ctx.drawImage(bitmap, 0, 0, width, height);

    // 释放 ImageBitmap 资源
    if (bitmap instanceof ImageBitmap) {
      bitmap.close();
    }

    const mimeType = isPng ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, mimeType, isPng ? undefined : quality);
    if (!blob) {
      return file;
    }

    // 生成压缩后的 File，保留原文件名（换后缀）
    const ext = isPng ? '.png' : '.jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const compressedFile = new File([blob], `${baseName}${ext}`, { type: mimeType });

    const originalKB = (file.size / 1024).toFixed(1);
    const compressedKB = (compressedFile.size / 1024).toFixed(1);
    const ratio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
    console.log(`[imageCompress] ${file.name} 压缩完成: ${originalKB}KB -> ${compressedKB}KB (压缩率 ${ratio}%), 尺寸 ${srcW}x${srcH} -> ${width}x${height}`);

    return compressedFile;
  } catch (err) {
    console.warn('[imageCompress] 压缩失败，降级使用原图:', err);
    return file;
  }
}

/**
 * 加载图片为 ImageBitmap 或 HTMLImageElement
 */
function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // 优先使用 createImageBitmap（性能更好）
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }

  // 降级方案：HTMLImageElement
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * 等比计算缩放后的尺寸
 * 如果原图最大边不超过 maxEdge，则不放大
 */
function calcScaledSize(srcW: number, srcH: number, maxEdge: number): { width: number; height: number } {
  const maxSrc = Math.max(srcW, srcH);
  if (maxSrc <= maxEdge) {
    return { width: srcW, height: srcH };
  }
  const scale = maxEdge / maxSrc;
  return {
    width: Math.round(srcW * scale),
    height: Math.round(srcH * scale),
  };
}

/**
 * Canvas 转 Blob
 */
function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      mimeType,
      quality,
    );
  });
}
